import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import { newDiscussionHref } from 'lib/community/discussions'
import { draftStorage } from 'lib/community/discussions-writer'
import {
  DRAFT_ANCHOR,
  DRAFT_COPY,
  draftCardStep,
  draftCardSummary,
  draftCountsLine,
  draftLastSavedLine,
  type DraftCardAction,
  type DraftCardStep,
  type DraftCardSummary,
} from 'lib/community/draft-card'
import base from './community.module.css'
import styles from './DraftCard.module.css'

export interface DraftCardViewProps {
  summary: DraftCardSummary
  step: DraftCardStep
  now: number
  /** The writer, which restores the stored draft. */
  href: string
  onStep: (action: DraftCardAction) => void
  className?: string
}

/** "Your draft" as a pure view of one step (shown / confirm / deleted). */
export const DraftCardView: React.VFC<DraftCardViewProps> = ({
  summary,
  step,
  now,
  href,
  onStep,
  className,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const deleteRef = useRef<HTMLButtonElement>(null)
  const previous = useRef(step)
  // Focus follows the inline confirmation: into it on "Delete draft", back on Cancel.
  useEffect(() => {
    if (previous.current === 'shown' && step === 'confirm') {
      cancelRef.current?.focus()
    } else if (previous.current === 'confirm' && step === 'shown') {
      deleteRef.current?.focus()
    }
    previous.current = step
  }, [step])

  if (step === 'deleted') {
    return (
      <p id={DRAFT_ANCHOR} className={clsx(styles.deleted, className)} role="status">
        {DRAFT_COPY.deleted}
      </p>
    )
  }
  const counts = draftCountsLine(summary)
  const saved = draftLastSavedLine(summary.savedAt, now)
  const meta = [counts, saved].filter(Boolean).join(' · ')
  return (
    <section
      id={DRAFT_ANCHOR}
      className={clsx(styles.card, className)}
      aria-labelledby="draft-title"
    >
      <p className={styles.eyebrow}>
        {DRAFT_COPY.title}
        <span className={styles.kind}>{summary.kindLabel}</span>
      </p>
      <h2 id="draft-title" className={styles.title}>
        {summary.title}
      </h2>
      {summary.excerpt && <p className={styles.excerpt}>{summary.excerpt}</p>}
      {meta && <p className={styles.meta}>{meta}</p>}
      <p className={styles.note}>{DRAFT_COPY.deviceNote}</p>
      {step === 'confirm' ? (
        <div className={styles.actions} role="group" aria-label={DRAFT_COPY.delete}>
          <span className={styles.confirm}>{DRAFT_COPY.confirm}</span>
          <button type="button" className={styles.danger} onClick={() => onStep('confirm')}>
            {DRAFT_COPY.confirmYes}
          </button>
          <button
            ref={cancelRef}
            type="button"
            className={styles.secondary}
            onClick={() => onStep('cancel')}
          >
            {DRAFT_COPY.confirmNo}
          </button>
        </div>
      ) : (
        <div className={styles.actions}>
          <Linked href={href} className={clsx(base.btn, styles.primary)}>
            {DRAFT_COPY.continue}
          </Linked>
          <button
            ref={deleteRef}
            type="button"
            className={styles.secondary}
            onClick={() => onStep('ask')}
          >
            {DRAFT_COPY.delete}
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * "Your draft" (the hub Overview, owner only; `/profile`): the discussion draft this browser
 * holds, read after mount ONLY — the hub and `/profile` HTML are the same for every reader
 * (Directive 897af2f1), so the server renders nothing here. Nothing at all without a non-blank
 * draft. Arriving on `#draft` scrolls here once it shows (it mounts after the page's own anchor
 * scroll already ran).
 */
const DraftCard: React.VFC<{ className?: string }> = ({ className }) => {
  const [summary, setSummary] = useState<DraftCardSummary | null>(null)
  const [step, setStep] = useState<DraftCardStep>('shown')
  const [now, setNow] = useState(0)
  const scrolled = useRef(false)

  useEffect(() => {
    setSummary(draftCardSummary(draftStorage.load()))
    setNow(Date.now())
  }, [])

  const shown = summary !== null
  useEffect(() => {
    if (shown && !scrolled.current && window.location.hash === `#${DRAFT_ANCHOR}`) {
      scrolled.current = true
      document.getElementById(DRAFT_ANCHOR)?.scrollIntoView({ block: 'start' })
    }
  }, [shown])

  if (!summary) {
    return null
  }
  const onStep = (action: DraftCardAction): void => {
    const next = draftCardStep(step, action)
    if (next === 'deleted' && step !== 'deleted') {
      draftStorage.clear()
    }
    setStep(next)
  }
  return (
    <DraftCardView
      summary={summary}
      step={step}
      now={now}
      href={newDiscussionHref({}, publicConfig('useTrailingSlash'))}
      onStep={onStep}
      className={className}
    />
  )
}

export default DraftCard
